import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import { difference } from '../../geo/clip';
import { ringBounds } from '../../geo/polygon';
import type { PaletteModel } from '../../data/types';
import type { ChunkSink } from '../mesh/chunker';
import type { VertexStyle } from '../mesh/meshBuilder';
import type { DensifyConfig } from '../terrain/densify';
import { groundStyle } from '../terrain/groundStyle';
import { PATTERN } from '../../render/patterns/patternIds';
import { WORLD } from '../../core/config';
import type { PavingPiece } from './pavingPieces';
import { withHeightRefinement } from '../terrain/heightRefinement';

/*
 * The ground's own style for the piece it lies in, so the colour, the surface and the edge id stay
 * the street ground's and no line is drawn between fronts, with look-2's paving pattern on top. The
 * field classes reach no street or square piece, so none are needed. The seed goes in extent[0]
 * (STYLE section 15.1).
 */
function pavingStyle(piece: PavingPiece, palette: PaletteModel): VertexStyle {
  return {
    ...groundStyle(piece.ground, palette, {}),
    pattern: PATTERN.streetPaving,
    extent: [piece.seed, 0],
  };
}

/**
 * Every piece of paving on the ground plane, refined as the ground is so the two share their
 * outlines, with metres along and across its street as the pattern's coordinates.
 */
export function drawPaving(
  pieces: readonly PavingPiece[],
  palette: PaletteModel,
  chunks: ChunkSink,
  densify?: DensifyConfig,
  heightAt?: (p: Vec2) => number,
  dedicatedDecks: readonly PolygonWithHoles[] = []
): { readonly pieces: number; readonly triangles: number } {
  let triangles = 0;
  const relief = heightAt ? withHeightRefinement(densify, heightAt) : undefined;
  const decks = dedicatedDecks.map((polygon) => ({ polygon, box: ringBounds(polygon.outer) }));
  for (const piece of pieces) {
    const builder = chunks.builderAt('infrastructure', piece.polygon.outer[0]!);
    const before = builder.triangleCount;
    const box = ringBounds(piece.polygon.outer);
    const near = decks
      .filter(
        ({ box: deck }) =>
          deck.minX <= box.maxX &&
          deck.maxX >= box.minX &&
          deck.minY <= box.maxY &&
          deck.maxY >= box.minY
      )
      .map(({ polygon }) => polygon);
    const polygons = near.length ? difference([piece.polygon], near) : [piece.polygon];
    for (const polygon of polygons)
      builder.groundPolygon(
        pavingStyle(piece, palette),
        polygon,
        relief?.heightAt ?? WORLD.groundY,
        relief?.config ?? densify,
        piece.coordsAt
      );
    triangles += builder.triangleCount - before;
  }
  return { pieces: pieces.length, triangles };
}
